/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import { dayjs } from './shared/date';
import { type Dayjs } from 'dayjs';

import { RISK_FREE_RATE } from './BlackScholes';
import { fetchRawAPI } from './shared/fetch';
import { decode } from '@msgpack/msgpack';
import {
  ET,
  formatAsPercentage,
  getCurrentDate,
  predicateSearch,
  findNearestIdx,
  roundToStartOfDay,
  DAY_IN_MS,
  formatAsDelta,
  spansNonMktDays,
  CLOSURES,
} from './shared';
import * as d3 from 'd3';
import {
  OPTION_IDX,
  RawGreeksDataMap,
  GreeksData,
  GREEK_IDX,
  RawGreeksObject,
  RawStatsData,
  TermStructure,
  RawStatsObject,
  STAT_IDX,
  ExpirationsDisplayType,
  StrikeDisplayType,
  VolSkew,
  MatrixColumnData,
  MatrixData,
  DailyGreeks,
  Point,
  VolSkewReferenceLine,
} from '../types';

import {
  calculateImpliedVolatilities,
  calculateVarianceTime,
  VarianceTimeCalendarModel,
  black76,
} from 'sg-risk-engine';
import { stockMarketClose, stockMarketOpen } from './shared/date';
import { getRiskEngineConstants } from 'config/shared';

export const DEFAULT_STATS_LOOKBACK = '60';
const { CALL, PUT } = OPTION_IDX;

export const ROW_WIDTH = 100;
export const ROW_HEIGHT = 40;
export const PAGE_OFFSET_BOTTOM = 10;
export const VSKEW_DEFAULT_DTE = 30;

export const IVOL_STATS_KEY = 'stats';
export const SKEW_PREM_KEY = 'skew-prem';
export const DELTA_NORMED_KEY = 'delta-normed';
export const DELTA_KEY = 'delta';
export const THETA_KEY = 'theta';
export const THETA_PERCENT_KEY = 'theta-percent';

const SKEW_PRICE_THRESHOLD_MULTIPLIER = 0.000_009;

const BISECTION_IV_LO = 1e-4;
const BISECTION_IV_HI = 10.0;
const BISECTION_MAX_ITER = 50;
const BISECTION_PRICE_EPS = 1e-6;

function black76IVBisection(
  s: number,
  k: number,
  t: number,
  r: number,
  price: number,
  isCall: boolean,
): number {
  if (s <= 0 || k <= 0 || t <= 0 || price <= 0) {
    return NaN;
  }

  let lo = BISECTION_IV_LO;
  let hi = BISECTION_IV_HI;

  for (let i = 0; i < BISECTION_MAX_ITER; i++) {
    const sig = (lo + hi) / 2;
    const px = black76(s, k, t, r, sig, isCall).price;
    if (px < price) {
      lo = sig;
    } else {
      hi = sig;
    }
    if (Math.abs(px - price) < BISECTION_PRICE_EPS) {
      return (lo + hi) / 2;
    }
  }

  return (lo + hi) / 2;
}

export const getMidpointPrice = (greeksData: number[]): number => {
  return (greeksData[GREEK_IDX.BID] + greeksData[GREEK_IDX.ASK]) / 2.0;
};

export const getDateFromUtc = (utcTime: string): Dayjs =>
  dayjs(Number(utcTime));

export const MIN_QUERY_DATE = dayjs('2023-09-18').tz(ET);

export const getGreeksFromMsgPack = async (rawMsgPackResponse: any) => {
  if (rawMsgPackResponse.status >= 300) {
    throw rawMsgPackResponse.error;
  }
  const arrayBuffer = await rawMsgPackResponse.arrayBuffer();
  const expirationMap: any = decode(arrayBuffer);
  return expirationMap as RawGreeksDataMap;
};

// Utility function to fetch data from the API
export const fetchRawFormattedData = async (
  endpoint: string,
): Promise<RawGreeksDataMap> => {
  const response = await fetchRawAPI(endpoint);
  return getGreeksFromMsgPack(response);
};

// Weight our implied volatility to the more out-of-the-money instrument.
// i.e.
//   At put delta -0.05, we take 0.95 * putIV + 0.05 * callIV
//        At delta 0.50, we take the average: (putIV + callIV) / 2
//   At call delta 0.05, we take 0.95 * callIV + 0.05 * putIV
export const getIVFromGreeks = (greeks: GreeksData) => {
  const callDelta = greeks[CALL][GREEK_IDX.DELTA];
  const idx = callDelta <= 0.5 ? CALL : PUT;
  const delta = Math.abs(greeks[idx][GREEK_IDX.DELTA]);
  return (
    greeks[idx][GREEK_IDX.VOLATILITY] * (1 - delta) +
    greeks[1 - idx][GREEK_IDX.VOLATILITY] * delta
  );
};

// utcTime1 is initial starting date
// utcTime2 is end date
export const convertToDaysTillExpiry = (
  utcTime1: number,
  utcTime2: number,
): number => {
  // rounding helps with ignoring the time of day so the DTE calculation is purely done on the basis of days in between
  // e.g. Getting DTE from 11/16 at 6pm to 11/17 8am should yield 1 DTE not 0 DTE
  const initialDate = dayjs(roundToStartOfDay(utcTime1));
  const endDate = dayjs(roundToStartOfDay(utcTime2));
  return endDate.diff(initialDate, 'day');
};

const ATM_DELTA = 0.5;

// Find the call option greek values at delta 50.  For Near-term expirations,
// this is essentially at "current price".  for long-term expirations, delta 50
// is at the forward price
export const findD50Greeks = (
  strikeMap: RawGreeksObject,
  idx: number,
): number => {
  const strikes: number[] = Object.keys(strikeMap)
    .map(Number)
    .sort((a, b) => a - b);

  // extract Call Deltas, delta idx = 1
  const deltas: number[] = strikes.map(
    (s) => strikeMap[s][CALL][GREEK_IDX.DELTA],
  );

  // extract Greek values
  const values: number[] = strikes.map((s) => strikeMap[s][CALL][idx]);

  const idxA = predicateSearch(deltas, (n) => n > ATM_DELTA);
  const idxB = Math.min(idxA + 1, deltas.length - 1);
  const fraction = (ATM_DELTA - deltas[idxB]) / (deltas[idxA] - deltas[idxB]);
  return d3.interpolateNumber(values[idxB], values[idxA])(fraction);
};

// Find the call option implied volatility at precisely delta 50
export const findD50IV = (strikeDataMap: RawGreeksObject): number => {
  return findD50Greeks(strikeDataMap, GREEK_IDX.VOLATILITY);
};

/**
 * Computes SpotGamma's At-The-Money (ATM) implied volatility for a given expiration.
 *
 * Finds the strikes bracketing `currentPrice`, extracts call/put midpoint prices,
 * calls the SG risk engine's `calculateImpliedVolatilities` for each bracketing strike,
 * and linearly interpolates to get the ATM IV.
 *
 * Naming breakdown: `computeSGAtMIV` = compute SpotGamma ATM IV
 * (the "M" stands for "Money" as part of the ATM convention).
 */
export const computeSGAtMIV = (
  strikeDataMap: RawGreeksObject,
  currentPrice: number,
  daysTillExpiration: number,
  fromDate: Dayjs,
  dividendDates: Float64Array,
  dividendAmounts: Float64Array,
): number => {
  const strikes: number[] = Object.keys(strikeDataMap)
    .map(Number)
    .sort((a, b) => a - b);

  const strikeIdx = Math.max(
    0,
    predicateSearch(strikes, (s) => s <= currentPrice),
  );

  const lowerStrike = strikes[strikeIdx];
  const upperStrike = strikes[Math.min(strikes.length - 1, strikeIdx + 1)];

  const lowerCallPrice = getMidpointPrice(strikeDataMap[lowerStrike][CALL]);
  const upperCallPrice = getMidpointPrice(strikeDataMap[upperStrike][CALL]);
  const lowerPutPrice = getMidpointPrice(strikeDataMap[lowerStrike][PUT]);
  const upperPutPrice = getMidpointPrice(strikeDataMap[upperStrike][PUT]);

  const fraction =
    upperStrike === lowerStrike
      ? 1
      : (currentPrice - lowerStrike) / (upperStrike - lowerStrike);

  const riskEngineConstants = getRiskEngineConstants();

  const ivResultsPair = calculateImpliedVolatilities(
    currentPrice,
    new Float64Array([lowerStrike, upperStrike]),
    new Float64Array([lowerCallPrice, upperCallPrice]),
    new Float64Array([lowerPutPrice, upperPutPrice]),
    daysTillExpiration / riskEngineConstants.ADJUSTED_DAYS_IN_YEAR,
    RISK_FREE_RATE,
    fromDate.unix(),
    Array.from(CLOSURES),
    dividendDates,
    dividendAmounts,
    false,
  );

  return d3.interpolateNumber(ivResultsPair[0], ivResultsPair[1])(fraction);
};

export const findD50Price = (strikeMap: RawGreeksObject): number => {
  const strikes: number[] = Object.keys(strikeMap)
    .map(Number)
    .sort((a, b) => a - b);
  const deltas: number[] = strikes.map(
    (s) => strikeMap[s][CALL][GREEK_IDX.DELTA],
  );
  const idxA = predicateSearch(deltas, (n) => n > ATM_DELTA);
  const idxB = Math.min(idxA + 1, deltas.length - 1);
  const fraction = (ATM_DELTA - deltas[idxB]) / (deltas[idxA] - deltas[idxB]);
  return d3.interpolateNumber(strikes[idxB], strikes[idxA])(fraction);
};

export const findParityPrice = (strikeMap: RawGreeksObject): number => {
  const strikes: number[] = Object.keys(strikeMap)
    .map(Number)
    .sort((a, b) => a - b);
  const priceDeltas: number[] = strikes.map(
    (s) =>
      getMidpointPrice(strikeMap[s][CALL]) -
      getMidpointPrice(strikeMap[s][PUT]),
  );
  const idxA = predicateSearch(priceDeltas, (p) => p >= 0);
  const idxB = Math.min(idxA + 1, priceDeltas.length - 1);
  const fraction =
    (0 - priceDeltas[idxB]) / (priceDeltas[idxA] - priceDeltas[idxB]);
  return d3.interpolateNumber(strikes[idxB], strikes[idxA])(fraction);
};

export const interpolateGreeksAtStrike = (
  strikeDataMap: RawGreeksObject,
  tgtStrike: number,
): GreeksData => {
  const strikes: number[] = Object.keys(strikeDataMap)
    .map(Number)
    .sort((a, b) => a - b);
  const idxA = predicateSearch(strikes, (s) => s <= tgtStrike);
  const idxB = Math.min(idxA + 1, strikes.length - 1);
  const interpolate = d3.interpolate(
    strikeDataMap[strikes[idxA]],
    strikeDataMap[strikes[idxB]],
  );
  const fraction =
    (tgtStrike - strikes[idxA]) / (strikes[idxB] - strikes[idxA]);
  return interpolate(fraction);
};

export const getForwardIV = (
  fromDate: Dayjs,
  currExp: number,
  prevExp: number,
  currIV: number,
  prevIV: number,
): number => {
  const currDTE = convertToDaysTillExpiryIntraday(fromDate.valueOf(), currExp);
  const prevDTE = convertToDaysTillExpiryIntraday(fromDate.valueOf(), prevExp);

  return Math.sqrt(
    (currDTE * Math.pow(currIV, 2) - prevDTE * Math.pow(prevIV, 2)) /
      (currDTE - prevDTE),
  );
};

export function findClosestGreeksExp(
  filteredGreeksMap: RawGreeksDataMap | null,
  tradeDate: dayjs.Dayjs,
): number {
  if (!filteredGreeksMap) {
    return -1;
  }

  const dateKeys = Object.keys(filteredGreeksMap);
  const dateEpochs = dateKeys.map((key) => Number(key));
  const tradeDateEpoch = tradeDate.utc().valueOf();

  let closestEpoch = 0;
  let smallestDiff = Infinity;
  for (const epoch of dateEpochs) {
    const diff = Math.abs(tradeDateEpoch - epoch);
    if (diff < smallestDiff) {
      smallestDiff = diff;
      closestEpoch = epoch;
    }
  }
  return closestEpoch;
}

export async function getPricesPerTradeDateMap(
  tradeDates: dayjs.Dayjs[],
  selectedSym: string,
  getClosingPrice: (
    symbol: string,
    date: string,
  ) => Promise<number | undefined>,
): Promise<Map<string, number>> {
  const uniqueTradeDates = [
    ...new Set(tradeDates.map((d) => d.format('YYYY-MM-DD'))),
  ];

  const pricesPerTradeDate = await Promise.all(
    uniqueTradeDates.map(async (date: string) => {
      const price = await getClosingPrice(selectedSym, date);
      if (price) {
        return { date, price };
      }
    }),
  );

  return new Map(
    pricesPerTradeDate
      .filter((v) => v !== undefined)
      .map((v) => [v.date, v.price]),
  );
}

export const extractTermStructureData = (
  expirationMap: RawGreeksDataMap,
  fromDate: Dayjs,
  timeFrame: string,
  statsDataMap: RawStatsData,
  currentPrice: number,
  useSpotGammaIV: boolean,
  dividendDates: Float64Array = new Float64Array(0),
  dividendAmounts: Float64Array = new Float64Array(0),
  lowerStat: number = STAT_IDX.P10,
  upperStat: number = STAT_IDX.P90,
): TermStructure[] => {
  const dataEntries: [string, RawGreeksObject][] =
    Object.entries(expirationMap);

  const statsMapEntries: [string, RawStatsObject][] =
    Object.entries(statsDataMap);

  const tteMs = statsMapEntries.map(([tte]: [string, RawStatsObject]) =>
    Number(tte),
  );

  const result: TermStructure[] = dataEntries.map(
    ([utcTime, strikeDataMap]: [string, RawGreeksObject], index: number) => {
      // strikeData is a map of key/value pairs where:
      // key is the strike for call/put
      // value is greeks data formatted as [call[], put[]] where each call/put array has 7 numbers
      let atmIV = findD50IV(strikeDataMap);

      // Convert date to days till expiry from the first date (which is either current day or user selected)
      const exp = Number(utcTime);
      const fromDateMarket = fromDate.tz(ET);
      const tte = exp - fromDateMarket.valueOf();
      const daysTillExpiration: number = convertToDaysTillExpiryIntraday(
        fromDateMarket.valueOf(),
        exp,
      );

      if (useSpotGammaIV) {
        atmIV = computeSGAtMIV(
          strikeDataMap,
          currentPrice,
          daysTillExpiration,
          fromDate,
          dividendDates,
          dividendAmounts,
        );
      }

      const termStructure: TermStructure = {
        daysToExpiry: daysTillExpiration,
        expirationDate: exp,
        [timeFrame]: atmIV,
      };

      if (index > 0) {
        let prevIdx = index - 1;
        let [prevExp, prevStrikeDataMap] = dataEntries[prevIdx];
        const cur = dayjs(exp);

        // If the previous expiration is under a week in length and we span a
        // weekend, find the closest previous expiration that is a week away from our current expiration
        let prev = dayjs(Number(prevExp));
        while (
          cur.diff(prev, 'days') < 7 &&
          spansNonMktDays(cur, prev) &&
          prevIdx > 0
        ) {
          [prevExp, prevStrikeDataMap] = dataEntries[--prevIdx];
          prev = dayjs(Number(prevExp));
        }

        let prevIV: number;
        if (useSpotGammaIV) {
          const prevDaysTillExpiration = convertToDaysTillExpiryIntraday(
            fromDateMarket.valueOf(),
            Number(prevExp),
          );
          prevIV = computeSGAtMIV(
            prevStrikeDataMap,
            currentPrice,
            prevDaysTillExpiration,
            fromDate,
            dividendDates,
            dividendAmounts,
          );
        } else {
          prevIV = findD50IV(prevStrikeDataMap);
        }

        termStructure[`${timeFrame}/FIV`] = getForwardIV(
          fromDate,
          exp,
          Number(prevExp),
          atmIV,
          prevIV,
        );
      }

      // Find corresponding greeks object DTE closest to the stats entry dte
      const closestIdx = findNearestIdx(tteMs, tte);
      const statsEntry: [string, RawStatsObject] = statsMapEntries[closestIdx];
      if (statsEntry == null) {
        // No stats to handle
        return termStructure;
      }

      // if delta 0.5 exists use it, otherwise find the next closest delta to 0.5
      const statsObj: RawStatsObject = statsEntry[1];
      let entry: number[] = statsObj[0.5];
      if (entry == null) {
        const normedStats: [number, number[]][] = Object.keys(statsObj)
          .map<[number, number[]]>((delta: string) => {
            const d = Number(delta);
            return [d < 0 ? 1 + d : d, statsObj[d]];
          })
          .sort(([d1]: [number, any], [d2]: [number, any]) => d1 - d2);

        const idx = findNearestIdx(
          normedStats.map((ns) => ns[0]),
          0.5,
        );
        entry = normedStats[idx][1];
      }

      // get lower and upper bounds, default to 10% & 90% percentiles
      // TODO: use state params to get the user configured stats
      const statsLowerBound = entry[lowerStat];
      const statsUpperBound = entry[upperStat];
      termStructure[`bounds-${timeFrame}`] = [statsLowerBound, statsUpperBound];

      return termStructure;
    },
  );

  return result;
};

// Dynamically merges 2 arrays of same object type with potentially different lengths.
// Using the value at the sharedKey attribute as the "entry" key, i.e. whether its a new entry or an existing one.
// Why make it generic? Because sometimes new objects need to be added while other times existing objects are to be updated
// For example: chart with 3 lines on it, each with x/y values, a 4th line is dynamically added but contains more x/y values,
// 1. Chart data is represented by JS Objects, we add "new" x/y values as new objects wit new key/value pairs, thus it needs it's own object key
// 2. Sometimes Y values for a certain line on the chart need to be simply updated
export const mergeObjectLists = <T extends object>(
  prevData: T[],
  newData: T[],
  sharedKey: keyof T,
): T[] => {
  if (prevData.length === 0) {
    return newData;
  }

  const mergedDataMap = new Map<any, T>();

  prevData.forEach((item) => mergedDataMap.set(item[sharedKey], { ...item }));

  newData.forEach((item) => {
    const existingItem = mergedDataMap.get(item[sharedKey]);
    mergedDataMap.set(item[sharedKey], { ...(existingItem || {}), ...item });
  });

  return Array.from(mergedDataMap.values());
};

const CURRENT_YEAR = dayjs().year();
export const getExpirationLabel = (expirationDateUtc: string) => {
  const date: Dayjs = dayjs.utc(Number(expirationDateUtc));
  const amLabel = `${date.hour() < 12 ? ' AM' : ''}`;

  if (date.year() === CURRENT_YEAR) {
    return `${date.format('MM-DD')} ${amLabel}`;
  }
  return `${date.format('YYYY-MM-DD')} ${amLabel}`;
};

// This function generates unique "field" key that can be used on line charts or anywhere
// to represent an exact selection of (TradeDate | ExpirationDate) pair
export const getVolSkewTimeframeString = (
  tradeDate: Dayjs,
  expirationDateUtc: string,
) => `${tradeDate.format('YYYY-MM-DD')}-${expirationDateUtc}`;

export const getVolSkewLineLabel = (
  tradeDate: Dayjs,
  expirationDateUtc: string,
  timezone: string,
) => {
  const label = getTradeDateDisplayText(tradeDate, timezone);

  const dte = getExpirationDisplayText(tradeDate, expirationDateUtc);

  return `${label} | ${dte}`;
};

export const getTradeDateDisplayText = (tradeDate: Dayjs, timezone: string) => {
  const label = tradeDate.isSame(getCurrentDate(), 'day')
    ? `Today ${getCurrentDate(timezone).format('LT')}`
    : tradeDate.format('YYYY-MM-DD');

  return label;
};

export const getExpirationDisplayText = (
  tradeDate: Dayjs,
  expirationDateUtc: string,
) => {
  const label = `${getExpirationLabel(
    expirationDateUtc,
  )}, ${convertToDaysTillExpiry(
    tradeDate.valueOf(),
    Number(expirationDateUtc),
  )} DTE`;

  return label;
};

export const getTermStructureKeyFromLabel = (
  expLabelType: ExpirationsDisplayType,
) => {
  if (expLabelType === ExpirationsDisplayType.DaysToExpiration) {
    return 'daysToExpiry';
  } else {
    return 'expirationDate';
  }
};

export const getVolSkewTimeframeStringFromLabel = (
  strLabelType: StrikeDisplayType,
) => {
  if (strLabelType === StrikeDisplayType.FixedStrike) {
    return 'strike';
  } else if (strLabelType === StrikeDisplayType.Moneyness) {
    return 'moneyness';
  } else {
    return 'delta-x';
  }
};

const MIN_SLOPE = 0.001;

export const getSnapshotTime = (greeksMap: RawGreeksDataMap): number => {
  const dataEntries: [string, RawGreeksObject][] = Object.entries(greeksMap);
  return Math.max(
    ...dataEntries.map(([, rawObj]: [string, RawGreeksObject]) => {
      return Math.max(
        ...Object.values(rawObj).map((greeks: GreeksData) =>
          Math.max(
            greeks[CALL][GREEK_IDX.TIMESTAMP],
            greeks[PUT][GREEK_IDX.TIMESTAMP],
          ),
        ),
      );
    }),
  );
};

export const getStatsFromExp = (
  statsDataObj: RawStatsData,
  greeksMap: RawGreeksDataMap,
  utcExp: string,
): RawStatsObject => {
  const tteMs = Object.keys(statsDataObj)
    .map(Number)
    .sort((a, b) => b - a);

  const snapTime = getSnapshotTime(greeksMap);

  const exp = Number(utcExp);
  let idx = predicateSearch(tteMs, (t: number) => exp <= snapTime + t);
  if (
    idx + 1 < tteMs.length &&
    Math.abs(snapTime + tteMs[idx + 1] - exp) <
      Math.abs(snapTime + tteMs[idx] - exp)
  ) {
    ++idx;
  }
  return statsDataObj[tteMs[idx]];
};

// this function returns a map of expirationDate (greeks) -> statsObj (RawStatsObject with delta -> stats entries)
export const getExpStatsMap = (
  statsDataObj: RawStatsData,
  greeksMap: RawGreeksDataMap,
): Map<number, RawStatsObject> => {
  return new Map(
    Object.keys(greeksMap).map((utcExp: string) => {
      const exp = Number(utcExp);
      const statsObj = getStatsFromExp(statsDataObj, greeksMap, utcExp);

      return [exp, statsObj];
    }),
  );
};

// Convert put delta to call delta
export const normDelta = (delta: number) => (delta < 0 ? 1 + delta : delta);

const getNormedDeltas = (
  deltaStatsMap: RawStatsObject,
): [number, number[]][] => {
  return Object.entries(deltaStatsMap)
    .map<[number, number[]]>(([key, stats]: [string, number[]]) => {
      const n = Number(key);
      return [normDelta(n), stats]; // convert put delta to call delta
    })
    .sort(([ak, _av], [bk, _bv]) => bk - ak);
};

// x,y coordinate inputs for quintile bezier curve defining the normed-delta's
// relationship to the x-axis.  start: 0,0. end: 1,1 (ala CSS bezier
// animations), for a total of 6 points.
const A = { x: 0.0, y: 0.55 };
const B = { x: 0.15, y: 0.2 };
const C = { x: 0.9, y: 0.5 };
const D = { x: 1.0, y: 0.3 };

// Tweaked quintic bezier function with y inverted to accomodate normed-delta moving from 1 to 0
const deltaBezier = (t: number): Point => {
  const x =
    5 * t * (1 - t) ** 4 * A.x +
    10 * t ** 2 * (1 - t) ** 3 * B.x +
    10 * t ** 3 * (1 - t) ** 2 * C.x +
    5 * t ** 4 * (1 - t) * D.x +
    t ** 5;
  const y =
    5 * t * (1 - t) ** 4 * A.y +
    10 * t ** 2 * (1 - t) ** 3 * B.y +
    10 * t ** 3 * (1 - t) ** 2 * C.y +
    5 * t ** 4 * (1 - t) * D.y +
    t ** 5;
  return { x, y: 1 - y };
};

// x'(t). Used in Newton-Raphson-based finder logic in findBezierPoint.
const bezierDX = (t: number): number =>
  (5 - 25 * t) * (1 - t) ** 3 * A.x +
  -10 * t * (5 * t - 2) * (1 - t) ** 2 * B.x +
  10 * t ** 2 * (5 * t ** 2 - 8 * t + 3) * C.x +
  5 * (4 - 5 * t) * t ** 3 * D.x +
  5 * t ** 4;

// y'(t).  Used in Newton-Raphson-based finder logic in findBezierPoint.
const bezierDY = (t: number): number =>
  -(
    (5 - 25 * t) * (1 - t) ** 3 * A.y +
    -10 * t * (5 * t - 2) * (1 - t) ** 2 * B.y +
    10 * t ** 2 * (5 * t ** 2 - 8 * t + 3) * C.y +
    5 * (4 - 5 * t) * t ** 3 * D.y +
    5 * t ** 4
  );

const EPSILON = 1e-9;

// Take a delta target (x) or an x-axis target (y) and return the mapped point.
const findBezierPoint = (tgt: { x?: number; y?: number }): Point => {
  const key = tgt.x != null ? 'x' : 'y';
  const derivative = tgt.x != null ? bezierDX : bezierDY;

  // use x or (1 - y) as initial best guesses of t (i.e. x = y line)
  let t: number = tgt.x ?? 1 - tgt.y!;
  let pt = deltaBezier(t);
  let iter = 0;
  while (Math.abs(pt[key] - tgt[key]!) > EPSILON) {
    const deriv = derivative(t);
    t += (tgt[key]! - pt[key]) / deriv;
    pt = deltaBezier(t!);
    if (++iter > 20) {
      // this should be impossible with a monotically increasing Bezier curve
      // (as we've chosen), but make sure we never hit an infinite loop because
      // Newton-Raphson didn't converge
      console.error('Delta Bezier did not converge');
      break;
    }
  }
  return pt;
};

// Map ("normed") delta to the x-axis in such a way that we shrink x-axis real-estate
// near-ATM delta and elongate x-axis space in more OTM deltas
export const getDeltaX = (delta: number) => {
  return findBezierPoint({ x: delta }).y;
};

export const getDeltaFromX = (x: number) => {
  return findBezierPoint({ y: x }).x;
};

export const getReferenceLineKey = (
  referenceLine: VolSkewReferenceLine,
  timeFrame: string,
) => {
  return `${referenceLine}-${timeFrame}`;
};

/**
 * Converts the time difference between two dates to days,considering the start date
 * at market open and end date at market close for 0 DTE to correctly calculate the
 * fractional day between start and end.
 *
 * @param startDate - The start date in epoch milliseconds.
 * @param endDate - The end date in epoch milliseconds.
 * @returns The number of days between the two dates.
 */
export const convertToDaysTillExpiryIntraday = (
  startDate: number,
  endDate: number,
): number => {
  const start = stockMarketOpen(dayjs(startDate));
  const end = stockMarketClose(dayjs(endDate));

  return end.diff(start, 'day', true /* floating point: don't truncate */);
};

export const extractVolSkewData = (
  strikesMap: RawGreeksObject,
  timeFrame: string,
  deltaStatsMap: RawStatsObject | null,
  currentPrice: number,
  useSpotGammaIV: boolean,
  timeFrameStart: Dayjs,
  timeFrameExpiration: Dayjs,
  dividendDates: Float64Array = new Float64Array(0),
  dividendAmounts: Float64Array = new Float64Array(0),
): VolSkew[] => {
  if (strikesMap == null) {
    return [];
  }
  const strikeMapEntries: [string, GreeksData][] = Object.entries(strikesMap);

  let fwdPrice = findD50Price(strikesMap);
  if (isNaN(fwdPrice)) {
    fwdPrice = findParityPrice(strikesMap);
  }
  const priceThreshold = SKEW_PRICE_THRESHOLD_MULTIPLIER * fwdPrice;

  const filteredEntries = strikeMapEntries.filter(
    ([_strike, callPutArray]: [string, GreeksData], idx, entries) => {
      const callDelta = callPutArray[CALL][GREEK_IDX.DELTA];
      if (callDelta >= 0.05 && callDelta <= 0.95) {
        return true;
      }
      const optionIDX = callDelta > 0.5 ? PUT : CALL;
      const inst = callPutArray[optionIDX];
      if (idx < entries.length - 1 || idx > 0) {
        const delta = inst[GREEK_IDX.DELTA];
        if (Math.abs(delta) < 0.001) {
          return false;
        }

        const neighborIDX = idx === entries.length - 1 ? idx - 1 : idx + 1;
        const [_ns, nextEntry] = entries[neighborIDX];
        const nextInst = nextEntry[optionIDX];
        const nextVol = nextInst[GREEK_IDX.VOLATILITY];
        const run = nextInst[GREEK_IDX.DELTA] - delta;
        const rise = nextVol - callPutArray[optionIDX][GREEK_IDX.VOLATILITY];
        const slope = rise / run;
        return Math.abs(slope) > MIN_SLOPE;
      }
      // If there is no neighbor against which to form a slope, use priceThreshold
      return getMidpointPrice(callPutArray[optionIDX]) >= priceThreshold;
    },
  );

  const strikes = filteredEntries.map(([strike, _]) => Number(strike));
  const [callPrices, putPrices] = [
    filteredEntries.map(([_, greeks]) => getMidpointPrice(greeks[CALL])),
    filteredEntries.map(([_, greeks]) => getMidpointPrice(greeks[PUT])),
  ];

  const riskEngineConstants = getRiskEngineConstants();
  const daysTillExpiration = convertToDaysTillExpiryIntraday(
    timeFrameStart.valueOf(),
    timeFrameExpiration.valueOf(),
  );

  const yte = calculateVarianceTime(
    timeFrameStart.unix(),
    Array.from(CLOSURES),
    daysTillExpiration * riskEngineConstants.SECONDS_PER_DAY,
    VarianceTimeCalendarModel.CalendarDayModel,
  );

  const ivResults = useSpotGammaIV
    ? calculateImpliedVolatilities(
        currentPrice,
        new Float64Array(strikes),
        new Float64Array(callPrices),
        new Float64Array(putPrices),
        yte,
        RISK_FREE_RATE,
        timeFrameStart.unix(),
        Array.from(CLOSURES),
        dividendDates,
        dividendAmounts,
        true,
      )
    : [];

  const result: VolSkew[] = filteredEntries.map(
    ([strike, callPutArray]: [string, GreeksData], index) => {
      const callDelta = callPutArray[CALL][GREEK_IDX.DELTA];
      const putDelta = callPutArray[PUT][GREEK_IDX.DELTA];
      const delta = callDelta <= 0.5 ? callDelta : 1 + putDelta;
      const iv = getIVFromGreeks(callPutArray);

      const strikeNumber = Number(strike);
      const moneyness = Number((strikeNumber / fwdPrice).toFixed(3));

      const skew: VolSkew = {
        strike: strikeNumber,
        moneyness,
        [`${timeFrame}-${DELTA_KEY}`]: delta,
        'delta-x': getDeltaX(delta),
        [`${timeFrame}`]: useSpotGammaIV ? ivResults[index] : iv,
      };

      if (deltaStatsMap == null || delta < 0.001 || delta > 0.999) {
        return skew;
      }

      const normedEntries = getNormedDeltas(deltaStatsMap);
      const normedDeltas = normedEntries.map(([d, _stats]) => d);

      // TODO: Investigate the results of the following logic. (This would
      // extrapolate IV values using slope of the first entries past their bounds)
      // statsIdx = Math.max(0, predicateSearch(normedDeltas, (d) => d >= delta));
      const statsIdx = predicateSearch(normedDeltas, (d) => d >= delta);

      if (statsIdx >= 0) {
        const stats = normedEntries[statsIdx][1];

        // Define all stat mappings for cleaner iteration
        const statMappings = [
          [VolSkewReferenceLine.P10_IV, STAT_IDX.P10],
          [VolSkewReferenceLine.P25_IV, STAT_IDX.P25],
          [VolSkewReferenceLine.P75_IV, STAT_IDX.P75],
          [VolSkewReferenceLine.P90_IV, STAT_IDX.P90],
          [VolSkewReferenceLine.MedianIV, STAT_IDX.MEDIAN],
          [VolSkewReferenceLine.AvgIV, STAT_IDX.MEAN],
          [VolSkewReferenceLine.MinIV, STAT_IDX.MIN],
          [VolSkewReferenceLine.MaxIV, STAT_IDX.MAX],
        ] as const;

        // Add all available stats for this timeframe
        statMappings.forEach(([baseline, statIdx]) => {
          skew[`${getReferenceLineKey(baseline, timeFrame)}`] = stats[statIdx];
        });

        // Keep the bounds for backward compatibility
        skew[`bounds-${timeFrame}`] = [
          stats[STAT_IDX.P10],
          stats[STAT_IDX.P90],
        ];

        if (statsIdx + 1 < normedDeltas.length) {
          const nextStats = normedEntries[statsIdx + 1][1];
          const nextDelta = normedDeltas[statsIdx + 1];
          const fraction =
            (delta - nextDelta) / (normedDeltas[statsIdx] - nextDelta);

          // Interpolate all stats using the same mappings
          statMappings.forEach(([baseline, statIdx]) => {
            const key = getReferenceLineKey(baseline, timeFrame);
            skew[key] = d3.interpolate(nextStats[statIdx], skew[key])(fraction);
          });

          // Update bounds with interpolated values
          skew[`bounds-${timeFrame}`] = [
            skew[
              `${getReferenceLineKey(VolSkewReferenceLine.P10_IV, timeFrame)}`
            ],
            skew[
              `${getReferenceLineKey(VolSkewReferenceLine.P90_IV, timeFrame)}`
            ],
          ];
        }
      }
      return skew;
    },
  );
  return result;
};

export const isStrikeWithinRange = (
  currentPrice: number,
  strike: number,
  range: number,
) => {
  return (
    currentPrice == null ||
    (strike >= currentPrice * (1 - range) &&
      strike <= currentPrice * (1 + range))
  );
};

export const MATRIX_SETTINGS_ZOOMED_NORMAL = {
  expColumnWidth: ROW_WIDTH,
  cellColumnWidth: ROW_WIDTH,
  height: ROW_HEIGHT,
  fontSize: '12px',
  transform: 'none',
};

export const MATRIX_SETTINGS_ZOOMED_OUT = {
  expColumnWidth: ROW_WIDTH,
  cellColumnWidth: ROW_WIDTH * 0.15,
  height: ROW_HEIGHT * 0.5,
  fontSize: '11px',
  transform: 'rotate(-45deg)',
};

// More than 3 keys means there is at least one IV value remaining along with the date
// after deletion of the key
export const isNonEmptyChartDataObject = (obj: VolSkew | TermStructure) =>
  Object.keys(obj).length > 3;

export const getFilteredGreeksData = (
  rawData: RawGreeksDataMap,
  date?: string,
): RawGreeksDataMap => {
  const currentDate = getCurrentDate();
  const snapshotTime = getSnapshotTime(rawData);
  const filteredData: Record<string, [number, RawGreeksObject]> = {};
  // Ensure we process expiration dates in order and overwrite any AM expirations with the PM expiration on the same day.
  for (const expiry of Object.keys(rawData)
    .map(Number)
    .sort((a, b) => a - b)) {
    const expirationDate: Dayjs = dayjs(expiry);
    if (
      !expirationDate.isBefore(date ?? currentDate, 'day') &&
      expiry > snapshotTime
    ) {
      filteredData[expirationDate.format('YYYY-MM-DD')] = [
        expiry,
        rawData[expiry],
      ];
    }
  }
  return Object.fromEntries(Object.values(filteredData));
};

// Type for risk-free rate getter function: takes YTE (years to expiration) and returns rate
export type RiskFreeRateGetter = (yte: number) => number;

export const buildMatrixTableData = (
  fromDate: Dayjs,
  expStrikeMap: RawGreeksDataMap,
  matrixCompareMode: boolean,
  matrixPrevExpiryMap: RawGreeksDataMap | null,
  expStatsMap: Map<number, RawStatsObject> | null,
  currentPrice: number,
  useSpotGammaIV: boolean,
  getRiskFreeRate: RiskFreeRateGetter = () => RISK_FREE_RATE,
  dividendDates: Float64Array = new Float64Array(0),
  dividendAmounts: Float64Array = new Float64Array(0),
) => {
  const colMap: Map<string, MatrixColumnData> = new Map();

  const transformedData: MatrixData[] = [...Object.entries(expStrikeMap)]
    .map(([expiry, strikeMap]: [string, RawGreeksObject]) => {
      // "Forward price" is the correct reference price to use for comparing iVol for a given expiry
      const fwdPrice = findD50Price(strikeMap);
      // Call and Put greeks interpolated for fwd price as the strike. Used for calculating skew price
      const atmGreeks = interpolateGreeksAtStrike(strikeMap, fwdPrice);

      // year till expiration, 3rd parameter set to true allows dayjs to return a float value
      const yte = dayjs(Number(expiry)).diff(fromDate, 'year', true);

      // Backout implied vol from market prices using WASM Black-76 bisection
      const rfr = getRiskFreeRate(yte);
      const atmCallIV = black76IVBisection(
        currentPrice,
        fwdPrice,
        yte,
        rfr,
        getMidpointPrice(atmGreeks[CALL]),
        true,
      );
      const atmPutIV = black76IVBisection(
        currentPrice,
        fwdPrice,
        yte,
        rfr,
        getMidpointPrice(atmGreeks[PUT]),
        false,
      );

      const rowData: MatrixData = {
        expiryDate: Number(expiry),
        id: expiry,
      };

      const deltaStatsMap: RawStatsObject | undefined = expStatsMap?.get(
        Number(expiry),
      );

      const [callPrices, putPrices] = [
        Object.values(strikeMap).map((greeks: GreeksData) =>
          getMidpointPrice(greeks[CALL]),
        ),
        Object.values(strikeMap).map((greeks: GreeksData) =>
          getMidpointPrice(greeks[PUT]),
        ),
      ];

      const ivResults = useSpotGammaIV
        ? calculateImpliedVolatilities(
            currentPrice,
            new Float64Array(Object.keys(strikeMap).map(Number) ?? []),
            new Float64Array(callPrices),
            new Float64Array(putPrices),
            yte,
            getRiskFreeRate(yte),
            fromDate.tz(ET).unix(),
            Array.from(CLOSURES),
            dividendDates,
            dividendAmounts,
            false,
          )
        : [];

      // loop through each strike
      Object.entries(strikeMap).forEach(
        ([strikePrice, greeks]: [string, GreeksData], index: number) => {
          const callDelta = greeks[CALL][GREEK_IDX.DELTA];
          const putDelta = greeks[PUT][GREEK_IDX.DELTA];
          const idx = callDelta <= 0.5 ? CALL : PUT;
          const otmTheta = greeks[idx][GREEK_IDX.THETA];

          rowData[strikePrice] = useSpotGammaIV
            ? ivResults[index]
            : getIVFromGreeks(greeks);

          rowData[`${strikePrice}-${DELTA_KEY}`] = greeks[idx][GREEK_IDX.DELTA];
          rowData[`${strikePrice}-${THETA_KEY}`] = otmTheta;
          rowData[`${strikePrice}-${THETA_PERCENT_KEY}`] =
            otmTheta / getMidpointPrice(greeks[idx]);

          const tgtDelta = callDelta > 0.5 ? 1 + putDelta : callDelta;

          // Figure out stats entry
          if (deltaStatsMap != null && tgtDelta >= 0.001 && tgtDelta <= 0.999) {
            let stats: number[] | null = null;
            const normedEntries = getNormedDeltas(deltaStatsMap);
            const normedDeltas = normedEntries.map(([d, _stats]) => d);
            const statsIdx = predicateSearch(
              normedDeltas,
              (d) => d >= tgtDelta,
            );

            // It's possible we can't find our target delta within stats on the wings
            if (statsIdx >= 0) {
              stats = normedEntries[statsIdx][1];
              if (statsIdx + 1 < normedDeltas.length && stats) {
                const nextDelta = normedDeltas[statsIdx + 1];
                const fraction =
                  (tgtDelta - nextDelta) / (normedDeltas[statsIdx] - nextDelta);
                const interpolate = d3.interpolate(
                  normedEntries[statsIdx + 1][1],
                  stats,
                );
                stats = interpolate(fraction);
              }
            }
            rowData[`${strikePrice}-${IVOL_STATS_KEY}`] = stats;
          }

          // Calculate Skew Premium using WASM Black-76.
          //   1. Get price of option using ATM ivol.
          //   2. Subtract that from mkt price.
          const strike = Number(strikePrice);
          const isCall = idx === CALL;
          const iVol = isCall ? atmCallIV : atmPutIV;
          const bsmPrice = black76(
            currentPrice, // s: current stock price
            strike, // k: strike price
            yte, // t: time to expiration
            getRiskFreeRate(yte), // r: risk-free rate
            iVol, // sigma: volatility
            isCall, // isCall: true for call, false for put
          ).price;
          rowData[`${strikePrice}-${SKEW_PREM_KEY}`] =
            getMidpointPrice(greeks[idx]) - bsmPrice;
          rowData[`${strikePrice}-${DELTA_NORMED_KEY}`] = tgtDelta;

          if (!colMap.has(strikePrice)) {
            // Add the strike price as a column if it doesn't already exist
            colMap.set(strikePrice, {
              dataKey: strikePrice,
              label: strikePrice,
              numeric: true,
            });
          }
        },
      );
      return rowData;
    })
    .filter(
      // filter dates that don't exist in prev map when comparing
      (row) =>
        !matrixCompareMode || matrixPrevExpiryMap?.[row.expiryDate] != null,
    );
  // sort columns by strike, aka datakey
  const finalColumns = [...colMap.values()].sort(
    (a: MatrixColumnData, b: MatrixColumnData) =>
      Number(a.dataKey) - Number(b.dataKey),
  );
  // add first column to be expiry date
  finalColumns.unshift({
    dataKey: 'expiryDate',
    label: 'Expiry | Strike',
  });

  return {
    matrixData: transformedData,
    matrixColumnData: finalColumns,
  };
};

export const getNextAvailableColor = (
  usedColors: string[],
  availableColors: string[],
): string | undefined =>
  availableColors.find((color: string) => !usedColors.includes(color));

export const getTimeAdjustedIvValue = (
  value: number,
  fromDte: number,
  toDte: number,
) => value * Math.sqrt(fromDte / toDte);

export const closestExpirationIdx = (
  termStructureData: TermStructure[],
  termStructureSelectedGreeks: DailyGreeks[],
  daysFromNow = 90,
) => {
  const maxSnapshotTime = Math.max(
    ...termStructureSelectedGreeks.map((tsGreeks: DailyGreeks) =>
      getSnapshotTime(tsGreeks.data),
    ),
  );

  const targetDateUtc: number = dayjs(maxSnapshotTime)
    .add(daysFromNow, 'day')
    .valueOf();

  const closestIdx = predicateSearch(
    termStructureData,
    (ts) => ts.expirationDate < targetDateUtc,
  );

  return closestIdx < 0 ? termStructureData.length - 1 : closestIdx;
};

export const getGroupedPayloads = (
  differentiatorKey: string,
  data: any[],
): Record<string, any[]> =>
  data.reduce((agg, item) => {
    const baseKey = item.dataKey.replace(differentiatorKey, '');
    agg[baseKey] = agg[baseKey] ?? [];
    agg[baseKey].push(item);
    return agg;
  }, {});

export const findClosestExpiration = (
  snapTime: number,
  expirations: string[],
  targetDte: number,
) => {
  const dtes = expirations.map((e) => Number(e) - snapTime);
  const target = targetDte * DAY_IN_MS;
  const idx = findNearestIdx(dtes, target);
  return expirations[Math.max(0, idx)];
};

export const SKEW_TICK_FORMATTERS = {
  delta: formatAsDelta,
  'delta-x': (x: number) => formatAsDelta(getDeltaFromX(x)),
  moneyness: formatAsPercentage,
  strike: (v: number) => `$${v.toLocaleString('en-US')}`,
};

export const SKEW_VIEW_TOOLTIPS = {
  [StrikeDisplayType.Moneyness]:
    'The option strike price divided by the stock price; when greater than 100%, this represents a strike that is higher than the current price. When less than 100%, this is a strike that is lower than the current price.',
  [StrikeDisplayType.Delta]:
    "Delta measures the sensitivity of an option's price to movement in the underlying stock; the delta value determines how much is made or lost as the underlying stock's price moves.",
  [StrikeDisplayType.FixedStrike]:
    'The option strike price for an underlying ETF, stock, or index.',
};

export const MATRIX_DATA_SETTING_TOOLTIPS = {
  zScore:
    'The difference in standard deviations between the Implied Volatility of one cell and the statistical mean with the same corresponding expiry time and delta.',
  statsMode:
    'Displays a color gradient indicating how high (green) or low (red) implied volatility is compared to the statistical mean',
  statsLookback:
    'In Statistical Mode, you can specify a look back period of 30, 60, or 90 days to use for the calculated statistics.',
  skewPrem:
    'Displays the premium due to volatility skew for the strike and expiration.',
  compareMode:
    'Compare Implied Volatility between two historical snapshots; A red cell indicates that Implied Volatility of the reference date is lower than that of the comparison date. Green indicates Implied Volatility is higher for the reference date.',
  absoluteMode:
    'The SpotGamma teal color gradient shows how high or low Implied Volatility is for each cell relative to other strikes and expirations.',
  highlightsMode:
    'Identifies potentially mispriced options based on the Implied Volatility levels of adjacent strikes and expirations.',
  sgIV: 'SpotGamma Implied Volatility is a calculation for implied volatility.',
};
